import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { socket } from "../utils/socket";
import { RootState, AppDispatch } from "../redux/store";
import {
  addNotification,
  clearNotifications,
  fetchAdminNotifications,
  fetchUserNotifications,
} from "../redux/slices/notificationSlice";

const ADMIN_ROLES = ["ADMIN", "SUPER_ADMIN", "REVIEWER"];

/**
 * Gère tout le cycle de vie du socket en fonction de l'état auth :
 *  - Connexion avec JWT dans handshake.auth
 *  - Enregistrement sécurisé côté backend (register → backend vérifie JWT)
 *  - Adhésion à la room admin pour les profils admin
 *  - Écoute des events NOTIFICATION / ADMIN_NOTIFICATION → Redux
 *  - Déconnexion et nettoyage au logout
 */
export const useSocketSetup = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!currentUser) {
      // Utilisateur déconnecté : couper le socket et vider les notifs
      if (socket.connected) socket.disconnect();
      dispatch(clearNotifications());
      return;
    }

    const token = localStorage.getItem("authToken") || "";
    const roleCode = currentUser.role?.role_code ?? "";
    const isAdmin = ADMIN_ROLES.includes(roleCode);

    // Injecter le JWT dans le handshake avant connexion
    socket.auth = { token };

    // ---- Handlers ----

    const onConnect = () => {
      // S'enregistrer — le backend extrait l'userId depuis le JWT
      socket.emit("register");

      // Si admin, rejoindre la room dédiée
      if (isAdmin) {
        socket.emit("join_admin");
      }

      // Charger les notifications initiales depuis l'API
      if (isAdmin) {
        dispatch(fetchAdminNotifications());
      } else {
        dispatch(fetchUserNotifications());
      }
    };

    const onNotification = (data: any) => {
      dispatch(addNotification(data));
    };

    const onAdminNotification = (data: any) => {
      if (isAdmin) dispatch(addNotification(data));
    };

    socket.on("connect", onConnect);
    socket.on("NOTIFICATION", onNotification);
    socket.on("ADMIN_NOTIFICATION", onAdminNotification);

    // Se connecter (ou reconnecter avec le nouveau token si déjà connecté)
    if (socket.connected) {
      // Déjà connecté : forcer un re-register avec le token actuel
      onConnect();
    } else {
      socket.connect();
    }

    return () => {
      socket.off("connect", onConnect);
      socket.off("NOTIFICATION", onNotification);
      socket.off("ADMIN_NOTIFICATION", onAdminNotification);
    };
  }, [currentUser?.id]);
};
